import { TFunction } from 'i18next'

// Kept in sync with validate_password/1 in Sunergos.Schema.User (server/lib/sunergos/schema/user.ex):
// at least PASSWORD_MIN_LENGTH characters, and at least one lowercase letter, one uppercase letter
// and one digit. Checking here lets the form show the requirement in the field before any request
// is sent; the server validates the same thing regardless, and its errors still arrive as toasts.
export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_FORMAT = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).*/

// The error codes are the same ones the server returns for the same failures, so both
// paths share one translation.
export type PasswordError = 'PASSWORD_SHORT' | 'PASSWORD_REQUIREMENTS' | 'PASSWORDS_DIFFER'

/**
 * Check a new password against the server's password rule.
 */
export function validatePassword(password: string): PasswordError | null {
  // Count code points rather than UTF-16 units so the length agrees with the server's String.length/1.
  if (Array.from(password).length < PASSWORD_MIN_LENGTH) {
    return 'PASSWORD_SHORT'
  }
  if (!PASSWORD_FORMAT.test(password)) {
    return 'PASSWORD_REQUIREMENTS'
  }
  return null
}

/**
 * Check that the re-entered password matches the new password.
 */
export function validatePasswordAgain(password: string, passwordAgain: string): PasswordError | null {
  return password === passwordAgain ? null : 'PASSWORDS_DIFFER'
}

/**
 * Human readable, translated text for a password error.
 */
export function passwordErrorText(t: TFunction, error: PasswordError | null): string | null {
  switch (error) {
    case 'PASSWORD_SHORT':
      return t('Password must be at least {min} characters long.').replace('{min}', `${PASSWORD_MIN_LENGTH}`)
    case 'PASSWORD_REQUIREMENTS':
    case 'PASSWORDS_DIFFER':
      return t(error)
    default:
      return null
  }
}
